import { useEffect, useRef, useState } from 'react';
import { scrollToElementWithOffset } from '../utils/scroll';

const FEEDBACK_FADE_MS = 250;

export function useProgramSelection({
    datoer = [],
    initialSelectionByDate = {},
    defaultActiveDato = null,
    aktiviteterErApne = false,
    onSaveDay
}) {
    const [draftByDate, setDraftByDate] = useState({});
    const [savingByDate, setSavingByDate] = useState({});
    const [feedbackByDate, setFeedbackByDate] = useState({});
    const [feedbackVisibleByDate, setFeedbackVisibleByDate] = useState({});
    const [activeDato, setActiveDato] = useState(null);

    const feedbackRef = useRef(null);

    useEffect(() => {
        setDraftByDate(initialSelectionByDate);
    }, [initialSelectionByDate]);

    useEffect(() => {
        if (!datoer.length) {
            setActiveDato(null);
            return;
        }

        if (defaultActiveDato && datoer.includes(String(defaultActiveDato))) {
            setActiveDato(String(defaultActiveDato));
            return;
        }

        if (activeDato && datoer.includes(String(activeDato))) {
            return;
        }

        setActiveDato(datoer[0]);
    }, [datoer, defaultActiveDato, activeDato]);

    useEffect(() => {
        const currentFeedback = activeDato ? feedbackByDate[activeDato] : null;
        if (!activeDato || !currentFeedback) return;

        setFeedbackVisibleByDate(prev => ({
            ...prev,
            [activeDato]: false
        }));

        const showFrame = window.requestAnimationFrame(() => {
            setFeedbackVisibleByDate(prev => ({
                ...prev,
                [activeDato]: true
            }));
        });

        return () => {
            window.cancelAnimationFrame(showFrame);
        };
    }, [activeDato, feedbackByDate]);

    useEffect(() => {
        const currentFeedback = activeDato ? feedbackByDate[activeDato] : null;
        if (!currentFeedback || !feedbackRef.current) return;

        window.requestAnimationFrame(() => {
            scrollToElementWithOffset(feedbackRef.current, 230);
        });
    }, [activeDato, feedbackByDate]);

    const clearFeedback = dato => {
        setFeedbackVisibleByDate(prev => ({
            ...prev,
            [dato]: false
        }));

        window.setTimeout(() => {
            setFeedbackByDate(prev => ({
                ...prev,
                [dato]: null
            }));
        }, FEEDBACK_FADE_MS);
    };

    const toggleAktivitet = (dato, aktId) => {
        if (!aktiviteterErApne) return;

        if (feedbackByDate[dato]) {
            clearFeedback(dato);
        }

        setDraftByDate(prev => {
            const current = prev[dato] ?? [];
            const exists = current.includes(aktId);

            return {
                ...prev,
                [dato]: exists ? current.filter(id => id !== aktId) : [...current, aktId]
            };
        });
    };

    const saveDag = async dato => {
        if (!aktiviteterErApne) return;

        const checkedIds = draftByDate[dato] ?? [];

        setSavingByDate(prev => ({
            ...prev,
            [dato]: true
        }));

        const result = await onSaveDay(Number(dato), checkedIds);

        setSavingByDate(prev => ({
            ...prev,
            [dato]: false
        }));

        setFeedbackByDate(prev => ({
            ...prev,
            [dato]: {
                type: result.success ? 'success' : 'danger',
                message: result.message
            }
        }));
    };

    return {
        activeDato,
        setActiveDato,
        draftByDate,
        savingByDate,
        feedbackByDate,
        feedbackVisibleByDate,
        feedbackRef,
        toggleAktivitet,
        saveDag,
        clearFeedback
    };
}
